// AgentTemplateVideoModal 组件（新增 trigger 模式 + 默认示例视频）
import React, { useEffect, useRef, useState, cloneElement } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Video } from "@/components/common/video";
import AgentVideoController from "@/components/agent-modal-video/agent-video-controller";
import { cn } from "@/lib/utils";
import { Image } from "@/components/common/image";

export interface AgentTemplateVideoModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;           // 右上角样式标签（例如：杂志风格）
  trigger?: React.ReactElement; // 新增：支持触发模式
  videoUrl?: string;            // 改为可选，未传则使用示例地址
  poster?: string;
}

export const AgentTemplateVideoModal: React.FC<AgentTemplateVideoModalProps> = ({
  open,
  onOpenChange,
  title = "模板风格",
  trigger,
  videoUrl = "https://interactive-examples.mdn.mozilla.org/media/cc0-videos/flower.mp4",
  poster,
}) => {
  const SAMPLE_VIDEO_URL = "https://interactive-examples.mdn.mozilla.org/media/cc0-videos/flower.mp4";

  // 以窗口高度的 88% 作为内容高度，按 9:16 比例计算宽度
  const [contentHeight, setContentHeight] = useState(720);
  const [contentWidth, setContentWidth] = useState(Math.round(720 * 9 / 16));
  const videoRef = useRef<HTMLVideoElement>(null);

  // 新增：trigger 模式内部 open
  const [internalOpen, setInternalOpen] = useState(false);
  const isTriggerMode = !!trigger;
  const currentOpen = isTriggerMode ? internalOpen : Boolean(open);
  const handleOpenChange = (value: boolean) => {
    if (isTriggerMode) setInternalOpen(value);
    else onOpenChange?.(value);
  };
  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleOpenChange(true);
  };

  useEffect(() => {
    if (!currentOpen) return;
    const updateSize = () => {
      const h = Math.round(window.innerHeight * 0.88);
      const height = Math.max(560, Math.min(1080, h));
      const width = Math.round(height * 9 / 16);
      setContentHeight(height);
      setContentWidth(width);
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [currentOpen]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
      <Dialog.Content
        className={cn(
          "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
          "rounded-2xl shadow-lg overflow-hidden z-50",
          "bg-transparent"
        )}
        style={{ width: contentWidth, height: contentHeight }}
      >
        {/* 关闭按钮（右上角） */}
        <Dialog.Close asChild>
          <button
            className="absolute -top-12 right-0 w-9 h-9 rounded bg-black/50 hover:bg-black/60 text-white flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </Dialog.Close>

        {/* 视频容器 */}
        <div className="relative w-full h-full bg-black rounded-2xl overflow-hidden">
          {/* 样式标签（右上角） */}
          <div className="absolute top-3 right-3 px-3 py-1 rounded bg-black/60 text-white text-xs">
            {title}
          </div>

          {/* 视频内容 */}
          <div className="relative w-full h-full flex items-center justify-center">
            {videoUrl ? (
              <Video
                ref={videoRef}
                src={videoUrl}
                className="h-full w-auto"
                poster={poster}
                autoPlay
                preload="auto"
                controls={false}
              />
            ) : <Image src={poster} className="size-full object-contain" />}
          </div>

          {/* 播放控制条（底部覆盖） */}
          {videoUrl && <div className="absolute bottom-2 left-2 right-2">
            <AgentVideoController videoRef={videoRef} />
          </div>}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  );

  // 新增：trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              if (trigger.props.onClick) trigger.props.onClick(e);
              handleTriggerClick(e);
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // 受控模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
};

export default AgentTemplateVideoModal;